import type { CardTitle } from '../core/ids';
import type { TextKey } from './zh';

/**
 * **全游戏统一的文本组件系统。**
 *
 * 所有的界面文本（i18n 字典的每一条、卡牌的 `lore`）都由 `TextComponent[]` 描述，
 * 而不是裸字符串。这样做有两个目的：
 *
 * 1. **灵活**：牌引用存 id（改名/换语言自动跟着变）、术语按语言查词、
 *    动态值是 `param` 组件 —— 文案里**不留字符串占位符**。
 * 2. **极简富文本**：每个组件可选一个颜色档；颜色**优先于** UI 的
 *    `label({ fill })` 样式（tagged text 的 tag 样式盖过基础样式）。
 *
 * ## 怎么落地
 *
 * 这里只产出 **Pixi 的 tagged text**（`<accent>[捕获]</accent>` 这种），
 * 不 import Pixi、不碰调色板。标签到样式的映射在 `render/ui/richText.ts`。
 *
 * ⚠️ 它不是"lore 专用"的：`core/lore.ts` 只是这个系统的一个使用者。
 */

/** 颜色语义。具体色值由渲染层从 `INK` 取，这里只给"是哪一档" */
export type TextColor = 'default' | 'dim' | 'accent' | 'info' | 'warn' | 'danger' | 'ok';

/** 颜色档 → tagged text 的标签名。`null` = 不加标签（用正文色） */
export const TEXT_COLOR_TAG: Readonly<Record<TextColor, string | null>> = {
  default: null,
  dim: 'dim',
  accent: 'accent',
  info: 'info',
  warn: 'warn',
  danger: 'danger',
  ok: 'ok',
};

/** 纯文本片段（`type` 省略时就是它） */
export interface TextPlain {
  readonly type?: 'plain';
  readonly text: string;
  readonly color?: TextColor;
}
/** 动态值 —— 由 `t(key, params)` 的 params 填进来 */
export interface TextParam {
  readonly type: 'param';
  readonly name: string;
  readonly color?: TextColor;
}
/** 牌引用 —— 卡片名按当前语言解析，渲染时带中括号 */
export interface TextCard {
  readonly type: 'card';
  readonly cardId: CardTitle;
  readonly color?: TextColor;
}
/** 术语 —— 按当前语言查词，保证同一个概念永远同一个词 */
export interface TextTerm {
  readonly type: 'term';
  readonly term: string;
  readonly color?: TextColor;
}
/**
 * 嵌一段别的 i18n 键（复用长文案用）。
 *
 * ⚠️ 字典文件里对 `satisfies TextSpec` 的检查必须写在**对象字面量之外**
 * （单独一行），否则 `TextKey` 会被卷进「字典 → TextSpec → TextKey → 字典」的
 * 循环推断里，tsc 报 TS7022。见 `zh/menu.ts` 末尾那行。
 */
export interface TextKeyRef {
  readonly type: 'key';
  readonly key: TextKey;
  readonly color?: TextColor;
}

/** 一个片段。裸字符串 = 纯文本 */
export type TextComponent = string | TextPlain | TextParam | TextCard | TextTerm | TextKeyRef;
/** 一段文本 = 一串片段。**i18n 字典的每一条、lore 的每一层都是这个类型** */
export type TextSpec = readonly TextComponent[];

/** 动态值：可以直接是字符串/数字，也可以是一整段富文本（颜色/牌引用都能传） */
export type TextParamValue = string | number | TextSpec;
export type TextParams = Readonly<Record<string, TextParamValue>>;

/** 解析文本时需要的外部能力（由 i18n / 内容层提供） */
export interface TextEnv {
  /** 解析另一条 i18n 键。`rich` 决定要不要带 tagged 标签 */
  readonly key: (key: TextKey, params: TextParams, rich: boolean) => string;
  /** 牌引用怎么取显示名；不给就退化成"去掉包前缀的 id" */
  readonly card?: (id: CardTitle) => string;
  /** 术语怎么查词；不给就直接显示 term 名 */
  readonly term?: (term: string) => string;
}

/** 片段没给颜色时的默认档：牌引用/术语自带一档，纯文本用正文色 */
function defaultColor(part: TextComponent): TextColor {
  if (typeof part === 'string') return 'default';
  switch (part.type) {
    case 'card':
      return 'accent';
    case 'term':
      return 'info';
    default:
      return 'default';
  }
}

function fallbackCardName(id: CardTitle): string {
  return String(id).replace(/^[^:]+:/, '');
}

/** 一个片段 → 纯文本 */
function plainOf(part: TextComponent, params: TextParams, env: TextEnv): string {
  if (typeof part === 'string') return part;
  switch (part.type) {
    case 'param': {
      const value = params[part.name];
      if (value === undefined) return '';
      if (typeof value === 'string' || typeof value === 'number') return String(value);
      return textPlain(value, params, env);
    }
    case 'key':
      return env.key(part.key, params, false);
    case 'card':
      return '[' + (env.card ?? fallbackCardName)(part.cardId) + ']';
    case 'term':
      return env.term === undefined ? part.term : env.term(part.term);
    default:
      return part.text;
  }
}

function wrap(tag: string | null, body: string): string {
  return tag === null ? body : '<' + tag + '>' + body + '</' + tag + '>';
}

/** 一个片段 → tagged text（带颜色标签） */
function taggedOf(part: TextComponent, params: TextParams, env: TextEnv): string {
  if (typeof part === 'string') return part;
  let raw: string;
  if (part.type === 'param') {
    const value = params[part.name];
    if (value === undefined) return '';
    raw =
      typeof value === 'string' || typeof value === 'number'
        ? String(value)
        : textTagged(value, params, env);
  } else {
    raw = plainOf(part, params, env);
  }
  if (raw === '') return '';
  return wrap(TEXT_COLOR_TAG[part.color ?? defaultColor(part)], raw);
}

/** 把一段 `TextSpec` 解析成**当前语言的纯文本** */
export function textPlain(spec: TextSpec, params: TextParams, env: TextEnv): string {
  return spec.map((part) => plainOf(part, params, env)).join('');
}

/** 把一段 `TextSpec` 解析成 **Pixi tagged text**（颜色标签已就位） */
export function textTagged(spec: TextSpec, params: TextParams, env: TextEnv): string {
  return spec.map((part) => taggedOf(part, params, env)).join('');
}
